'use client'

import { forwardRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { CreateLocaleParams, LocaleType, UpdateLocaleParams } from '@/types/locale'
import { DirectionEnum, FormModeEnum } from '@/enums'
import { Separator } from '@/components/ui/separator'
import { LanguagesIcon, PlusCircleIcon, SaveIcon, XIcon } from 'lucide-react'
import { useUnsavedChangesGuard } from '@/hooks'
import { UnsavedChangesGuardDialog } from '@/components/partials'
import { setValidationErrors } from '@/lib/utils'

type PropsType = {
  initialData?: LocaleType | null
  onSubmit: (data: CreateLocaleParams | UpdateLocaleParams) => void
  onSubmitAndNew?: (data: CreateLocaleParams | UpdateLocaleParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type LocaleFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

const LocaleForm = forwardRef<LocaleFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()

    const localeSchema = z.object({
      name: z.string().min(1, t('not_blank')),
      code: z
        .string()
        .min(2, t('min_max_fixed', { min: 2 }))
        .max(2, t('min_max_fixed', { min: 2 })),
      countryCode: z.string().min(2, t('not_blank')),
      direction: z.enum([DirectionEnum.LTR, DirectionEnum.RTL]),
      active: z.boolean()
    })

    type FormData = z.infer<typeof localeSchema>

    const form = useForm<FormData>({
      resolver: zodResolver(localeSchema),
      defaultValues: {
        name: initialData?.name || '',
        code: initialData?.code || '',
        countryCode: initialData?.countryCode || '',
        direction: initialData?.direction || DirectionEnum.LTR,
        active: initialData?.active !== undefined ? initialData.active : true
      }
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
      },
      reset: () => form.reset()
    }))

    const handleSubmit = (data: FormData) => {
      onSubmit(data)
    }

    const handleSubmitAndNew = (data: FormData) => {
      onSubmitAndNew?.(data)
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LanguagesIcon className="h-5 w-5" />
            {mode === FormModeEnum.CREATE ? t('create_locale') : t('edit_locale')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit)}
              className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('name')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('name')} {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('code')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('code')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="countryCode"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel required>{t('country_code')}</FormLabel>
                      <FormControl>
                        <CountryCombobox
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder={t('select')}
                          error={!!fieldState.error}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="direction"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('direction')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl className="w-full">
                          <SelectTrigger>
                            <SelectValue placeholder={t('direction')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={DirectionEnum.LTR}>{t('ltr')}</SelectItem>
                          <SelectItem value={DirectionEnum.RTL}>{t('rtl')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-col items-start justify-start">
                      <div className="space-y-0.5">
                        <FormLabel>{t('active')}</FormLabel>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit)()}
                      disabled={isLoading || form.formState.isSubmitting}>
                      <SaveIcon />
                      <span className="hidden sm:inline">
                        {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                      </span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                    {isCreateModeWithSubmitAndNew ? <PlusCircleIcon /> : <SaveIcon />}
                    {isLoading || form.formState.isSubmitting
                      ? t('saving')
                      : isCreateModeWithSubmitAndNew
                        ? t('save_and_add_new')
                        : t(mode)}
                  </Button>
                </div>
              </div>
              <UnsavedChangesGuardDialog
                open={isNavigationBlocked}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    )
  }
)

LocaleForm.displayName = 'LocaleForm'

export default LocaleForm
